import React, { useEffect, useRef, useState } from "react"
import _ from "lodash"
// PR
import { DataTable } from "primereact/datatable"
import { Column } from "primereact/column"
import {ContextMenu} from "primereact/contextmenu"
// Components
import CarouselModal, { CarouselData } from "components/table/carousel/carousel-modal"
import CarouselTrigger from "components/table/carousel/carousel-trigger"
import { field as _field } from "components/table/lib/column-field"
import SearchHeader from "components/table/search-header"
import {
  TableAutomation,
  focusFirstCellByRowIndex,
  focusCellByContent,
  focusCellByDataId,
  pageToFirst,
  getPagingInfo,
  getFocusedRowIndex,
  unfocusAllCells } from "components/table/table-automation"
import { TextEdit } from "components/table/inputs"
import { DuplicateModal } from "components/modal/duplicate-confirmation"
// Controls
import Controls from "components/tenant-market/payout/controls/"
// Data
import automationService from "data/automation"
import carouselService from "data/carousel"
import payoutService from "data/payout"
import keyService, * as chord from "data/key"
import propertyService from "data/property"
import { handleReferences } from "data/lib/handle-references"
import { localizeNum } from "data/lib/numbers"
import { copy, paste, canPaste } from "data/lib/copy-paste"
// Styles
import { TMPayoutTable_s } from "components/tenant-market/payout/tm-payout-styles"

type ITMPayoutTable = {
  tableId: string
  data: object[]
  defaults: object
}

type ContextRow = {
  payoutid: number
  propertyid: number
  code: string
  systeminput?: string
}

const TMPayoutTable = (props: ITMPayoutTable) => {
  const usePrevious = (value) =>{
    const ref = useRef();
    useEffect(() => {
      ref.current = value;
    });
    return ref.current;
  }

  const traceMenuItem = {
    label: "Trace", icon: "pi pi-fw pi-search", command: e=> {
      const refreshFn = () => payoutService.fetchPayout(contextRow.propertyid)
      handleReferences("payout", contextRow.payoutid, {refreshFn});
    }
  }

  const nonSystemMenu = [
    {label: 'Copy Cell', icon: 'pi pi-fw pi-copy', command: e => { if(contextRow.systeminput === "systemImmutable") return;
      copy(contextCell, contextRow)
    }},
    {label: "Duplicate Row", icon: "pi pi-fw pi-copy", command: e => {
      payoutService.copy(contextRow.propertyid, contextRow.payoutid, contextRow.code)
    }},
    {label: 'Duplicate Row n Times', icon: 'pi pi-fw pi-clone', command: async e => { if(contextRow.systeminput) return;
      showDuplicateConfirmation(async (n) => {
        const payouts = await payoutService.copies(contextRow.propertyid, contextRow.payoutid, n)
      })
    }},
    {label: "Delete Row", icon: "pi pi-fw pi-trash", command: e => {
      const currentRowNum = getFocusedRowIndex() + 1
      showDeleteConfirmation(contextRow, currentRowNum)
    }},
    traceMenuItem
  ]

  const systemMenu = [{...traceMenuItem}]

  const [carouselData, updateCarouselData] = useState<CarouselData|null>(null);
  const [searchFilter, setSearchFilter] = useState<string>("")
  const [contextRow, setContextRow] = useState<ContextRow>({payoutid: 0, propertyid: 0, code: ""})
  const [contextCell, setContextCell] = useState<object|null>(null)
  const [deleteConfirmation, setDeleteConfirmation] = useState<JSX.Element | null>(null)
  const [duplicateConfirmation, setDuplicateConfirmation] = useState<JSX.Element | null>(null)
  const menuRef = useRef<ContextMenu | null>(null)
  const [menu, setMenu] = useState<[]|object[]>(nonSystemMenu)
  const prevSearch = usePrevious(searchFilter);
  const tableRef = useRef() as React.MutableRefObject<DataTable>
  const field = _field("payout")

  useEffect(() => {
    const carouselSubscription = carouselService.stream().subscribe(data => {
      updateCarouselData(data);
    })

    const payoutidEventSubscription = payoutService.eventStream().subscribe(e => {
      switch(true) {
        case e.type === "PAYOUT_CREATED":
          setTimeout(() => pageToFirst(props.tableId), 1)
          setTimeout(() => {
            focusFirstCellByRowIndex(props.tableId, 0)
          }, 500)
          break
        case e.type === "PAYOUT_COPIED":
          setTimeout(() => {
            focusCellByContent(props.tableId, e.payload.code)
          }, 500)
      }
    })

    const keySubscription = keyService.stream().subscribe(k => {
      switch(true) {
        case k.chord === chord.CTRL_SHIFT_PLUS && k.down === true:
          if(!window[`${props.tableId}_carousel_in_focus`]) {
            unfocusAllCells()
            payoutService.create(propertyService.getStoredPropertyId())
          }
          break
        case k.chord === chord.CTRL_SHIFT_MINUS && k.down === true:
          if(!window[`${props.tableId}_carousel_in_focus`]) {
            const pager         = getPagingInfo(props.tableId);
            const currentRowNum = getFocusedRowIndex() + 1
            const records       = tableRef.current.props.value!;
            const currentRow    = records[(pager.currentPage > 1 ? (pager.currentPage - 1) * pager.pageSize : 0) + currentRowNum - 1];
            if (!currentRow.systeminput) showDeleteConfirmation({payoutid: currentRow.payoutid, propertyid: currentRow.propertyid, code: currentRow.code}, currentRowNum)
          }
          break
      }
    })

    return function cleanup() {
      carouselSubscription.unsubscribe()
      payoutidEventSubscription.unsubscribe()
      keySubscription.unsubscribe()
    }
  }, [])

  useEffect(() => {
    if (prevSearch !== "" && searchFilter === "") _.invoke(tableRef, "current.filter", "", "contains");
    else if (searchFilter !== "") _.invoke(tableRef, "current.filter", searchFilter, "contains");
    else return
  }, [searchFilter])

  useEffect(() => {
    !contextRow.systeminput || (contextRow.systeminput && !contextRow.systeminput.startsWith("system")) ? setMenu(nonSystemMenu) : setMenu(systemMenu)
  }, [contextRow])

  const persist = (value, field, rowData) => {
    let payout = _.omit(_.cloneDeep(rowData), 'index')

    if (field === "payoutdetail") {
      payout = value
    } else {
      _.set(payout, field, value)
    }

    payoutService.patch(payout)
    if(value && field === "payoutdetail") carouselService.updateRowData(payout)
  }

  useEffect(() => {
    if(canPaste(contextCell)) {
      nonSystemMenu.splice(1, 0, {label: 'Paste Cell', icon: 'pi pi-fw pi-file', command: e => { if(contextRow.systeminput) return;
        paste(contextCell, contextRow, payoutService.fetchPayout, 'payout')
      }})
    }
    contextRow.systeminput === "systemImmutable" ? setMenu(systemMenu) : contextRow.systeminput === "systemImmutable" ? setMenu(systemMenu) : setMenu(nonSystemMenu)
  }, [contextCell])

  const showDeleteConfirmation = (record: ContextRow, currentRow: number) => {
    automationService.broadcast("MODAL_OPENING", {})
    let pager = getPagingInfo(props.tableId)
    let row = currentRow + ((pager.currentPage - 1) * pager.pageSize);

    setDeleteConfirmation(Controls.DeleteConfirmation({
      show: true,
      close: () => {
        setDeleteConfirmation(null)
        setTimeout(() => automationService.broadcast("MODAL_CLOSING", {}), 200)
      },
      confirm: () => {
        payoutService.deleteOne(record.propertyid, record.payoutid)
        setDeleteConfirmation(null)
        automationService.broadcast("MODAL_CLOSING", {})
        setTimeout(() => focusFirstCellByRowIndex(props.tableId, currentRow - 2 > 0 ? currentRow - 2 : 0), 200)
      },
      rowNum: row,
      code: record.code
    }))
  }

  const showDuplicateConfirmation = (copy: (n: number) => Promise<void>) => {
    setDuplicateConfirmation(
      <DuplicateModal
        close={() => {setDuplicateConfirmation(null); focusCellByDataId( props.tableId, contextRow.payoutid)}}
        copy={copy}
        show={true}
        title={contextRow.code}
      />
    )
  }

  function updateContextMenu(originalEvent){
    let systemRows = document.querySelectorAll(".system-row, .systemImmutable-row");
    for (let i of systemRows){
      if (originalEvent.target === i || i.contains(originalEvent.target)) {
        setMenu(systemMenu);
      } else {
        if (!menu.length){
          setMenu(nonSystemMenu)
        }
      }
    }
    _.invoke(menuRef.current, "show", originalEvent)
  }

  const rowClassName = (rowData) => {
    if (rowData.systeminput && rowData.systeminput === "system"){
      return {"system-row" : rowData.systeminput === "system"}
    }
    else if (rowData.systeminput && rowData.systeminput === "systemImmutable"){
      return {"systemImmutable-row" : rowData.systeminput === "systemImmutable"}
    }
    else return {"" : rowData}
  }

  const defaults:object = props.defaults;

  return (
    <div>
      <SearchHeader setSearchFilter={setSearchFilter} />
      <ContextMenu model={menu} ref={menuRef} appendTo={document.querySelector("body")} onHide={() => {}}/>
      {defaults &&
      <TMPayoutTable_s id={`${props.tableId}-scrollable-container`}>
        <DataTable
          globalFilter={searchFilter}
          id={props.tableId}
          ref={tableRef}
          value={props.data}
          paginator={true}
          rows={20}
          rowsPerPageOptions={[10, 20, 50, 100, 500]}
          scrollable={true}
          frozenWidth="45px"
          paginatorPosition="bottom"
          rowClassName={rowClassName}
          onContextMenu={e => menuRef.current ? updateContextMenu(e.originalEvent) : null}
          contextMenuSelection={contextRow}
          onContextMenuSelectionChange={e => {
            setContextRow(e.value)
            setContextCell(e.originalEvent.target)
          }}
          stateStorage="custom"
          customSaveState={state => localStorage.setItem("primereact-table-preferences", JSON.stringify({rows: state.rows}))}
          customRestoreState={() => JSON.parse(localStorage.getItem("primereact-table-preferences")!)}
        >
          <Column header="#" frozen={true} style={{minWidth: "45px"}}
            body={(rowData, props) => props.rowIndex + 1}
          />
          <Column style={{minWidth: "250px"}} className="p-editable-column system-disabled"
            filter
            header="Code"
            {...field("code")}
            editor={props => TextEdit({persist, ...props})}
          />
          <Column style={{minWidth: "250px"}} className="p-editable-column system-disabled"
            filter
            header="Name"
            {...field("name")}
            editor={props => TextEdit({persist, ...props})}
          />
          <Column style={{minWidth: "250px"}} className="p-editable-column"
            header="Input Type"
            {...field("payoutdetail", (rowData, props) => { return (
              rowData.systeminput === "systemImmutable" ? <span style={{paddingLeft: 25}}>None</span> : <CarouselTrigger
                field="payoutdetail"
                rowData={rowData}
                title="Input Type"
                width={900}
                persist={persist}
                defaults={defaults}
                controls={{
                  basic: {control: Controls.InputTypeBasic, name: "Basic"},
                  detailed: {control: Controls.InputTypeDetailed, name: "Detailed"}
                }}
                componentAtRest={(props: {rowData: object, _field: string}) => {
                  let state = _.get(props.rowData, "inputtype")
                  let displayValue = ""
                  switch(true) {
                    case state === "basic":
                      let month = localizeNum(_.get(props.rowData, "mosoffset"), 0)
                      displayValue = `Month: ${month || ""}`
                      break
                    case state === "detailed":
                      displayValue = "Detailed"
                      break
                  }
                  return <span>{displayValue}</span>
                }}
                hover={{
                  basic: [
                    {key: 'mosoffset', label: 'Month', ignoreField: true}
                  ],
                  detailed: {
                    single: [],
                    multiple: [
                      {key: 'month', label: 'Month'},
                      {key: 'percent', label: 'Percent'}
                    ]
                  }
                }}
              />)},
              (err) => !_.includes(["code", "name"], err.path[0])
            )}
          />
        </DataTable>
        { carouselData && carouselData["show"] && <CarouselModal code="code" data={carouselData} key="carousel-modal" width={500} /> }
        { deleteConfirmation }
        { duplicateConfirmation }
        { props.data.length ? <TableAutomation tableId={props.tableId}/> : null }
      </TMPayoutTable_s>}
    </div>
  )
}

export default TMPayoutTable
